import axios from 'axios';
import type { OpenAIResponse, PromptMessage } from './types';
import { getApiKey } from '@/utils/apiKeys';
import { User } from '@/types/common';
import { ApiKeyService } from '@/hooks/useApiKeys';

const CHAT_COMPLETIONS_API_URL = 'https://openrouter.ai/api/v1/chat/completions';
const MODELS_API_URL = 'https://openrouter.ai/api/v1/models';

export interface OpenRouterModel {
  id: string;
  name: string;
  created?: number;
  description?: string;
  context_length?: number;
  architecture?: {
    modality?: string;
    input_modalities?: string[];
    output_modalities?: string[];
  };
  pricing?: {
    prompt?: string;
    completion?: string;
  };
  supported_parameters?: string[];
}

interface OpenRouterModelsResponse {
  data?: OpenRouterModel[];
}

export async function fetchOpenRouterModels(): Promise<OpenRouterModel[]> {
  const response = await axios.get<OpenRouterModelsResponse>(MODELS_API_URL);

  return (response.data.data ?? [])
    .filter(model => {
      const outputModalities = model.architecture?.output_modalities ?? [];
      return outputModalities.length === 0 || outputModalities.includes('text');
    })
    .sort((a, b) => (b.created ?? 0) - (a.created ?? 0));
}

export async function fetchOpenRouterAnswer(
  messages: PromptMessage[],
  model: string,
  user: User | null
): Promise<OpenAIResponse> {
  if (!user) {
    throw new Error('User not authenticated');
  }

  const OPENROUTER_API_KEY = getApiKey(ApiKeyService.OPENROUTER, user.id);

  if (!OPENROUTER_API_KEY) {
    throw new Error('OpenRouter API key not configured');
  }

  try {
    const response = await axios.post(
      CHAT_COMPLETIONS_API_URL,
      {
        model,
        messages: messages.map(message => ({
          role: message.role === 'developer' ? 'system' : message.role,
          content: message.content,
        })),
        temperature: 0.7,
        max_tokens: 1000,
      },
      {
        headers: {
          'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Interview Helper',
        }
      }
    );

    if (!response.data?.choices?.[0]?.message?.content) {
      throw new Error('Invalid response format from OpenRouter API');
    }

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('OpenRouter API Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw new Error(`OpenRouter API Error: ${error.response?.data?.error?.message || error.message}`);
    }
    throw error;
  }
}
